// Producer Page wizard helpers: step list, per-step "can continue" rules, preview model, logo URLs.

export const STEPS = Object.freeze([
  { id: 'title', label: 'Title' },
  { id: 'about', label: 'About Me' },
  { id: 'products', label: 'Products Offered' },
  { id: 'licenses', label: 'State Licenses' },
  { id: 'carriers', label: 'Carriers' },
  { id: 'contact', label: 'Contact' },
  { id: 'review', label: 'Review & Save' },
]);

export const NIPR_URL = 'https://nipr.com/licensing-center';
export const NIPR_STEPS = Object.freeze([
  'Go to nipr.com/licensing-center.',
  'Select your licensed state.',
  'Follow the redirect to NAIC or Sircon.',
  'Log in using your National Producer Number (NPN) and last name.',
  'Click “Print License” to download or screenshot your active certificate.',
]);

/** The state-issued license number entered for `state`, or ''. */
export const licenseNumberOf = (page, state) => (page.stateLicenses || []).find((x) => x.state === state)?.licenseNumber || '';

/** `list` with `state`'s number set (blank removes it). Returns a new array. */
export function setLicenseNumber(list = [], state, value) {
  const rest = list.filter((x) => x.state !== state);
  return value.trim() ? [...rest, { state, licenseNumber: value }] : rest;
}

export const MAX_STATS = 4;
export const EMPTY_STAT = Object.freeze({ icon: 'users', title: '', description: '' });

/**
 * Logo for an issuing carrier, pulled automatically from its website domain.
 * The image falls back to a monogram in the UI if it fails to load.
 * @param {string} domain
 * @param {string} [logo] - Bundled logo path; wins over the domain lookup.
 * @returns {string}
 */
export function carrierLogoUrl(domain, logo) {
  if (logo) return logo;
  return domain ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128` : '';
}

/** @param {string} name @returns {string} Up to two initials for the monogram fallback. */
export function monogram(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

/** Page host label shown in the preview's address bar. */
export function previewAddress(url, slug) {
  if (url) return url.replace(/^https?:\/\//, '');
  return slug ? `civixcrm.ai/p/${slug}` : 'civixcrm.ai/p/your-page';
}

/** True when every business stat the agent started has a title and description. */
export function statsValid(stats = []) {
  return stats.every((s) => s.title.trim() && s.description.trim());
}

/** Every filled social link is a full http(s) URL. */
export function socialsValid(socials = {}) {
  return Object.values(socials).every((v) => !v || /^https?:\/\/\S+$/i.test(v));
}

/**
 * Can the agent move past this step?
 * @param {string} stepId
 * @param {{page: object, assets: {licenses: {state: string}[]}, slug: {available?: boolean, checking?: boolean}, licensePhase?: string}} ctx
 * @returns {boolean}
 */
export function canContinue(stepId, { page, assets, slug, licensePhase = 'pick' }) {
  switch (stepId) {
    case 'title':
      return !!(page.title.trim() && page.headerTitle.trim() && page.slug && slug?.available === true && !slug?.checking && statsValid(page.stats));
    case 'about':
      return !!(page.about.name.trim() && page.about.occupation.trim() && page.about.text.trim()) && socialsValid(page.about.socials);
    case 'products':
      return page.products.length + page.customProducts.length > 0;
    case 'licenses': {
      if (licensePhase === 'pick') return !!(page.nationalProducerNumber.trim() && page.licenseStates.length);
      // Certificates and license numbers are optional: continue as soon as a state is chosen.
      return page.licenseStates.length > 0;
    }
    default:
      return true;
  }
}

/** Resolve selected product ids + custom products to display rows. */
export function resolveProducts(page, catalog) {
  const byId = new Map((catalog?.products || []).map((p) => [p.id, p]));
  return [...page.products.map((id) => byId.get(id)).filter(Boolean), ...page.customProducts];
}

/** Resolve selected carrier ids to catalog rows (name + domain), in the agent's order. */
export function resolveCarriers(page, catalog) {
  const byId = new Map((catalog?.carriers || []).map((c) => [c.id, c]));
  return page.carriers.map((id) => byId.get(id)).filter(Boolean);
}

/**
 * Build the model the shared page view renders, from the editor state.
 * @param {object} page - Editor page state.
 * @param {object} catalog
 * @param {{profile: boolean, background: boolean, licenses: {state: string}[]}} assets
 * @param {{profile?: string, background?: string, license?: (state: string) => string}} images - Preview image URLs.
 * @returns {object}
 */
export function previewModel(page, catalog, assets, images = {}) {
  const certs = new Map(assets.licenses.map((l) => [l.state, l]));
  const nameOf = (code) => catalog?.states?.find((s) => s.code === code)?.name || code;
  return {
    title: page.title,
    headerTitle: page.headerTitle,
    stats: page.stats,
    about: page.about,
    products: resolveProducts(page, catalog),
    carriers: resolveCarriers(page, catalog),
    nationalProducerNumber: page.nationalProducerNumber,
    // Every selected state, with whatever credentials were provided (same shape as the public page).
    licenses: page.licenseStates.map((s) => ({
      state: s,
      name: nameOf(s),
      licenseNumber: licenseNumberOf(page, s),
      hasCertificate: certs.has(s),
      certificateType: certs.get(s)?.mime === 'application/pdf' ? 'pdf' : 'image',
    })),
    showQuoteForm: page.showQuoteForm,
    bookingUrl: '',
    hasProfile: assets.profile,
    hasBackground: assets.background,
    images,
  };
}

/** Normalise an API page into the shape the editor edits (no undefined fields). */
export function normalizePage(p = {}, catalog = null) {
  return {
    ...p,
    slug: p.slug || '',
    title: p.title || '',
    headerTitle: p.headerTitle || '',
    stats: (p.stats || []).map((s) => ({ icon: s.icon || 'users', title: s.title || '', description: s.description || '' })),
    about: { name: '', occupation: '', text: '', ...(p.about || {}), socials: { facebook: '', x: '', instagram: '', linkedin: '', youtube: '', tiktok: '', ...(p.about?.socials || {}) } },
    products: p.products || [],
    customProducts: p.customProducts || [],
    nationalProducerNumber: p.nationalProducerNumber || '',
    licenseStates: p.licenseStates || [],
    stateLicenses: (p.stateLicenses || []).map((x) => ({ state: x.state, licenseNumber: x.licenseNumber || '' })),
    // Drop carriers that are no longer offered so a stale id can never block saving.
    carriers: (p.carriers || []).filter((id) => !catalog?.carriers || catalog.carriers.some((c) => c.id === id)),
    showQuoteForm: p.showQuoteForm === true,
  };
}

/** Fields to send when saving (the server ignores everything else). */
export function savePayload(page) {
  const { slug, title, headerTitle, stats, about, products, customProducts, nationalProducerNumber, licenseStates, stateLicenses, carriers, showQuoteForm } = page;
  return { slug, title, headerTitle, stats, about, products, customProducts, nationalProducerNumber, licenseStates, stateLicenses, carriers, showQuoteForm };
}
